import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Text, Flex, Box } from "@radix-ui/themes";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSurveyStore, usePersistentSurveyStore } from "@store/survey";
import { Trans } from "@lingui/macro";
import { fetchSurveySection } from "@api/survey";
import Question from "@components/questions/Question";
import SurveySectionControls from "@components/SurveySectionControls";
import Loading from "@components/Loading";
import Error from "@components/Error";
import MarkdownDiv from "@components/MarkdownDiv";

const getNextSurveySectionSlug = (surveySections, surveySectionSlug, step) => {
  if (Object.keys(surveySections).length < 1) return;
  let currentSectionIndex = surveySections?.surveys?.findIndex(
    (section) => section.slug === surveySectionSlug,
  );
  return surveySections?.surveys[currentSectionIndex + step]?.slug;
};

export default function SurveyQuestions() {
  let { surveySectionSlug } = useParams();
  const surveySections = useSurveyStore((state) => state.surveySections);
  const setSurveySectionLayers = useSurveyStore(
    (state) => state.setSurveySectionLayers,
  );
  const [section, setSection] = useState();
  const queryClient = useQueryClient();
  const language = usePersistentSurveyStore((state) => state.language);

  const isDataInCache = (slug) => {
    return queryClient.getQueryData(["surveySectionQuestions", slug, language]);
  };

  const { isPending, isError, data, error, isFetching, isPlaceholderData } =
    useQuery({
      queryKey: ["surveySectionQuestions", surveySectionSlug, language],
      queryFn: () => {
        const cachedData = isDataInCache(surveySectionSlug);
        if (cachedData) {
          return Promise.resolve(cachedData);
        }
        return fetchSurveySection(surveySectionSlug);
      },
      placeholderData: keepPreviousData,
      staleTime: 180000,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
      refetchInBackground: false,
    });

  useEffect(() => {
    setSurveySectionLayers((state) => {
      const mapRef = state.getMapRef();
      try {
        state.surveySectionLayers.forEach((layer) => {
          const layerId = layer.layer.id;
          if (mapRef && mapRef.getLayer(layerId)) {
            mapRef.removeLayer(layerId);
          }
        });
      } catch (error) {
        console.error(error);
      }
      return data?.layers || [];
    });
  }, [data, setSurveySectionLayers]);

  useEffect(() => {
    if (!isPlaceholderData) {
      const nextSlug = getNextSurveySectionSlug(
        surveySections,
        surveySectionSlug,
        1,
      );

      if (nextSlug && !isDataInCache(nextSlug)) {
        queryClient.prefetchQuery({
          queryKey: ["surveySectionQuestions", nextSlug, language],
          queryFn: () => fetchSurveySection(nextSlug),
          staleTime: 180000,
          cacheTime: 300000,
        });
      }

      const prevSlug = getNextSurveySectionSlug(
        surveySections,
        surveySectionSlug,
        -1,
      );

      if (prevSlug && !isDataInCache(prevSlug)) {
        queryClient.prefetchQuery({
          queryKey: ["surveySectionQuestions", prevSlug, language],
          queryFn: () => fetchSurveySection(prevSlug),
          staleTime: 180000,
          cacheTime: 300000,
        });
      }
    }
  }, [
    data,
    isPlaceholderData,
    surveySections,
    surveySectionSlug,
    queryClient,
    language,
  ]);

  useEffect(() => {
    if (surveySections?.surveys?.length > 0) {
      const section = surveySections?.surveys?.find(
        (section) => section.slug === surveySectionSlug,
      );
      setSection(section);
    }
  }, [surveySections, surveySectionSlug]);

  if (isError)
    return (
      <Error>
        <Text color="red" size="1">
          <Trans
            id="SurveyQuestions.couldnt_load"
            variables={{ error: error.message }}
          >
            Could not load survey. {error.message}
          </Trans>
        </Text>
      </Error>
    );

  if (isPending || !section)
    return (
      <Loading>
        <Text size="2">
          <Trans id="SurveyQuestions.loading_questions">
            Loading questions
          </Trans>
        </Text>
      </Loading>
    );

  return (
    <div>
      <Box mb="4">
        <Box mr="5">
          {section.description && (
            <MarkdownDiv markdown={section.description} />
          )}
        </Box>
        <Flex direction="column" gap="3" pr="5" mt="1" mb="2">
          {data?.questions
            ?.sort((qa, qb) => (qa.order || -1) - (qb.order || -1))
            .map((question, idx) => {
              return (
                <Box key={`${idx}-${question.uuid}`}>
                  <Question question={question} />
                </Box>
              );
            })}
        </Flex>
      </Box>
      <Box mb="3" id="desktop-controls">
        <SurveySectionControls isFetching={isFetching} />
      </Box>
    </div>
  );
}
